import { Injectable, inject } from '@angular/core'
import { Client, IMessage, StompSubscription } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import { BehaviorSubject, Observable, Observer } from 'rxjs'
import { distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators'
import { environment } from '../../environments/environment'
import Keycloak from 'keycloak-js'

// Enum برای نگهداری وضعیت اتصال
export enum SocketClientState {
	ATTEMPTING,
	CONNECTED,
	DISCONNECTED,
}

@Injectable({
	providedIn: 'root',
})
export class WebSocketStomp {
	private readonly keycloakService = inject(Keycloak)
	private client: Client
	private state = new BehaviorSubject<SocketClientState>(SocketClientState.DISCONNECTED)

	constructor() {
		this.client = new Client({
			// به جای WebSocket معمولی، از SockJS برای سازگاری بهتر استفاده می‌کنیم
			webSocketFactory: () => new SockJS(`${environment.wsUrl}`),
			debug: () => {
				// console.log(new Date(), str)
			}, // لاگ برای دیباگ کردن
			reconnectDelay: 5000, // تلاش مجدد برای اتصال هر ۵ ثانیه
			heartbeatIncoming: 4000,
			heartbeatOutgoing: 4000,
		})

		// مانیتور کردن وضعیت اتصال برای اطلاع‌رسانی به بقیه اپلیکیشن

		// Fired when a connection is successfully established
		this.client.onConnect = frame => {
			// console.log('✅ STOMP Connected:', frame)
			this.state.next(SocketClientState.CONNECTED)
		}

		// Fired when the client is disconnected, either purposefully or due to an error
		this.client.onDisconnect = frame => {
			// console.log('❌ STOMP Disconnected:', frame)
			this.state.next(SocketClientState.DISCONNECTED)
		}

		// Fired when the broker sends a STOMP ERROR frame
		this.client.onStompError = frame => {
			// console.error('🛑 STOMP Broker Error:', frame.headers['message'], 'Additional details:', frame.body)
			this.state.next(SocketClientState.DISCONNECTED)
		}

		// Fired when the underlying WebSocket connection is closed for any reason
		// Refresh token before each reconnect to avoid stale JWT
		this.client.onWebSocketClose = () => {
			this.state.next(SocketClientState.DISCONNECTED)
			this.client.configure({
				connectHeaders: {
					Authorization: `Bearer ${this.keycloakService.token}`,
				},
			})
		}

		// Fired on a WebSocket error
		this.client.onWebSocketError = event => {
			// console.error('🌐 WebSocket Error:', event)
		}
	}

	/**
	 * اتصال به سرور وب‌سوکت با استفاده از توکن JWT در هدر.
	 */
	connect(): void {
		if (this.state.value === SocketClientState.CONNECTED || this.state.value === SocketClientState.ATTEMPTING) {
			// console.log('WebSocket is already connected or attempting to connect.')
			return
		}

		this.state.next(SocketClientState.ATTEMPTING)

		// ✅ روش جدید و امن احراز هویت: ارسال توکن در هدر اتصال
		this.client.configure({
			connectHeaders: {
				Authorization: `Bearer ${this.keycloakService.token}`,
			},
		})

		this.client.activate()
	}

	/**
	 * قطع اتصال از سرور.
	 */
	disconnect(): void {
		if (this.client.active) {
			this.client.deactivate()
		}
	}

	/**
	 * اشتراک در یک "مقصد" (topic) خاص و دریافت پیام‌های آن به صورت Observable.
	 * این متد به صورت خودکار منتظر برقراری اتصال می‌ماند و پس از هر قطع/اتصال مجدد،
	 * اشتراک را بازسازی می‌کند.
	 * @param topic مقصد مورد نظر (مثلاً /user/queue/notifications یا /topic/candles/1m/...)
	 */
	public subscribe<T>(topic: string): Observable<T> {
		// هر بار که وضعیت به CONNECTED تغییر کند (اولین اتصال + هر بازگشت قطعی)،
		// یک STOMP subscribe جدید ایجاد می‌شود.
		// distinctUntilChanged تضمین می‌کند که دو CONNECTED پشت سر هم فقط یک بار emit شوند.
		// وقتی consumer از Observable اصلی unsubscribe کند (مثلاً کامپوننت نابود شود)،
		// takeUntil(disconnect$) STOMP subscription را لغو می‌کند و pipe متوقف می‌شود.
		return this.state.pipe(
			distinctUntilChanged(),
			filter(state => state === SocketClientState.CONNECTED),
			switchMap(() => {
				// سیگنال قطع اتصال: وقتی وضعیت از CONNECTED خارج شود، STOMP subscription لغو شود
				const disconnect$ = this.state.pipe(filter(state => state !== SocketClientState.CONNECTED))

				return new Observable<T>((observer: Observer<T>) => {
					if (!this.client.active) {
						observer.complete()
						return
					}

					const subscription: StompSubscription = this.client.subscribe(topic, (message: IMessage) => {
						const body = JSON.parse(message.body)
						observer.next(body as T)
					})

					// وقتی disconnect$ emit کند یا consumer unsubscribe کند،
					// STOMP subscription لغو می‌شود
					return () => {
						subscription.unsubscribe()
					}
				}).pipe(takeUntil(disconnect$))
			})
		)
	}

	public getState(): Observable<SocketClientState> {
		return this.state.asObservable()
	}
}
